import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { lotteryApi } from '@/api/queries/lottery/lottery';
import { parseLotteryTicket } from '@/common/lib/parseLotteryTicket';
import { checkTimeUntilNextTicketDrawInfo } from '@/common/lib/time-until-next-ticket-draw-info';
import {
  type GetLotteryInfoResponseType,
  type LotterTierInProgressInfo,
  type LotteryTicketType,
  type LotteryTierInDrawInfo,
  type LotteryWinnerInfo,
  type MyLotteryTicketInfo,
  type MyUnclaimedLotteryTicketInfo,
} from '@/common/types/lottery';

interface LotteryInitialState {
  currentTier: {
    name: LotteryTicketType;
    inDraw?: boolean;
  };
  inDraw: LotteryTierInDrawInfo[];
  inProgress: LotterTierInProgressInfo[];
  isClaimRewards: boolean;
  myUnclaimedTickets: MyUnclaimedLotteryTicketInfo[];
  isLotteryHappeningNow?: boolean;
  showTicketDrawInfo: boolean;
  firstTicketReward?: MyLotteryTicketInfo;
}

const initialState: LotteryInitialState = {
  currentTier: {
    name: 'Silver',
  },
  inDraw: [],
  inProgress: [],
  isClaimRewards: false,
  myUnclaimedTickets: [],
  showTicketDrawInfo: checkTimeUntilNextTicketDrawInfo(),
  firstTicketReward: undefined,
};

const lotterySlice = createSlice({
  name: 'lottery',
  initialState,
  reducers: {
    changeCurrentTier: (
      state,
      { payload }: PayloadAction<LotteryTicketType>,
    ) => {
      state.currentTier.name = payload;
      state.currentTier.inDraw = !!state.inDraw.find(
        ({ tier }) => tier === payload,
      );
    },
    changeClaimLotteryRewardsStatus: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isClaimRewards = payload;
    },
    endLottery: (state) => {
      state.isLotteryHappeningNow = false;

      if (state.myUnclaimedTickets.length) {
        state.isClaimRewards = true;
      }
    },
    addLotteryWinner: (
      state,
      { payload }: PayloadAction<LotteryWinnerInfo>,
    ) => {
      const { type } = parseLotteryTicket(payload.ticket);

      const inDrawIndex = state.inDraw.findIndex(({ tier }) => tier === type);
      const inProgressIndex = state.inProgress.findIndex(
        ({ tier }) => tier === type,
      );

      if (!state.isLotteryHappeningNow) {
        state.isLotteryHappeningNow = true;
      }

      if (inDrawIndex >= 0) {
        const currentLottery = state.inDraw[inDrawIndex];
        currentLottery.winnerList.unshift(payload);

        return;
      }

      if (inProgressIndex >= 0) {
        const { id, tier, seasonId, startedAt, finishedAt, myTickets } =
          state.inProgress[inProgressIndex];
        const toInDraw: LotteryTierInDrawInfo = {
          id,
          tier,
          seasonId,
          startedAt,
          finishedAt,
          myTickets,
          drawn: false,
          winnerList: [payload],
        };
        state.inDraw.push(toInDraw);

        if (type === state.currentTier.name) {
          state.currentTier.inDraw = true;
        }
        state.inProgress.splice(inProgressIndex, 1);

        return;
      }
    },
    setFirstTicketReward: (
      state,
      { payload }: PayloadAction<MyLotteryTicketInfo | undefined>,
    ) => {
      state.firstTicketReward = payload;
    },
    addMyUnclaimedTickets: (
      state,
      { payload }: PayloadAction<MyUnclaimedLotteryTicketInfo>,
    ) => {
      state.myUnclaimedTickets.push(payload);
    },
    changeLotteryIsNowStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.isLotteryHappeningNow = payload;
    },
    setShowTicketDrawInfo: (state, { payload }: PayloadAction<boolean>) => {
      state.showTicketDrawInfo = payload;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      lotteryApi.endpoints.getLotteryInfo.matchFulfilled,
      (state, { payload }: PayloadAction<GetLotteryInfoResponseType>) => {
        state.inDraw = payload.inDraw;
        state.inProgress = payload.inProgress;
        state.myUnclaimedTickets = payload.myUnclaimedTickets;
        state.currentTier.inDraw = !!payload.inDraw.find(
          ({ tier }) => state.currentTier.name === tier,
        );

        if (payload.myUnclaimedTickets.length) {
          state.isClaimRewards = true;
        }
      },
    );
  },
});

export const {
  changeCurrentTier,
  changeClaimLotteryRewardsStatus,
  addLotteryWinner,
  addMyUnclaimedTickets,
  changeLotteryIsNowStatus,
  endLottery,
  setShowTicketDrawInfo,
  setFirstTicketReward,
} = lotterySlice.actions;
export default lotterySlice.reducer;
